import React, { useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, MessageCircle, Send, CheckCircle, AlertCircle } from 'lucide-react';
import { companyInfo } from '../utils/mockData';
import { useLanguage } from '../contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactSection = () => {
  const { t, language } = useLanguage();
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error
  const [errorMsg, setErrorMsg] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    language: 'english',
    message: '',
    website: '' // Honeypot - hidden field for spam bots
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');
    setErrorMsg('');
    try {
      await axios.post(`${API}/enquiries`, {
        name: formData.name.trim(),
        phone: formData.phone.trim(),
        email: formData.email.trim() || null,
        preferred_language: formData.language,
        message: formData.message.trim() || null,
        website: formData.website, // honeypot
      });
      setStatus('success');
      setFormData({ name: '', phone: '', email: '', language: 'english', message: '', website: '' });
      setTimeout(() => setStatus('idle'), 6000);
    } catch (err) {
      setStatus('error');
      const detail = err?.response?.data?.detail;
      if (Array.isArray(detail) && detail[0]?.msg) {
        setErrorMsg(detail[0].msg.replace(/^Value error, /, ''));
      } else if (typeof detail === 'string') {
        setErrorMsg(detail);
      } else {
        setErrorMsg(t(
          'Something went wrong. Please try again or call us directly.',
          '出错了，请稍后再试或直接致电我们。'
        ));
      }
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <section className="py-24 sm:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16">
          {/* Left: Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-2"
          >
            <div className="text-sm font-semibold text-eucalyptus-600 tracking-wider uppercase mb-4">
              {t('Get in Touch', '联系我们')}
            </div>
            <h2 className="text-4xl sm:text-5xl font-bold text-navy-900 mb-6 tracking-tight leading-tight">
              {t("Let's talk about your family", '聊聊您的家人')}
            </h2>
            <p className="text-lg text-gray-600 mb-10 leading-relaxed">
              {t(
                'A bilingual coordinator will reach out within one business day. Consultations are always free.',
                '双语协调员将在一个工作日内与您联系。咨询完全免费。'
              )}
            </p>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-2xl bg-eucalyptus-50 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5 text-eucalyptus-700" />
                </div>
                <div>
                  <div className="text-sm text-gray-500 mb-1">{t('Call us', '电话')}</div>
                  <a href={`tel:${companyInfo.phone}`} className="text-lg font-semibold text-navy-900 hover:text-eucalyptus-600 transition-colors" data-testid="contact-phone">
                    {companyInfo.phone}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-2xl bg-eucalyptus-50 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-eucalyptus-700" />
                </div>
                <div>
                  <div className="text-sm text-gray-500 mb-1">{t('Email', '邮箱')}</div>
                  <a href={`mailto:${companyInfo.email}`} className="text-lg font-semibold text-navy-900 hover:text-eucalyptus-600 transition-colors" data-testid="contact-email">
                    {companyInfo.email}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-2xl bg-eucalyptus-50 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-eucalyptus-700" />
                </div>
                <div>
                  <div className="text-sm text-gray-500 mb-1">{t('Visit us', '地址')}</div>
                  <div className="text-lg font-semibold text-navy-900">
                    {companyInfo.location}
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-4 p-5 -mx-2 rounded-2xl bg-eucalyptus-50 border-2 border-eucalyptus-200" data-testid="contact-wechat-block">
                <div className="flex-shrink-0 bg-white p-2 rounded-xl shadow-sm">
                  <img
                    src={companyInfo.wechatQR}
                    alt={`WeChat QR code for ${companyInfo.wechat}`}
                    className="w-24 h-24 sm:w-28 sm:h-28 object-cover rounded-lg"
                    loading="lazy"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <svg viewBox="0 0 24 24" className="w-5 h-5 text-eucalyptus-700 flex-shrink-0" fill="currentColor" aria-hidden="true">
                      <path d="M8.691 2C4.768 2 1.5 4.68 1.5 8.14c0 1.9 1.15 3.68 2.99 4.86l-.61 1.86 2.24-1.12c.62.09 1.24.14 1.87.14.16 0 .32-.01.48-.02-.09-.34-.14-.7-.14-1.06 0-3.16 2.99-5.72 6.68-5.72.11 0 .22 0 .33.01C14.94 4 12.05 2 8.69 2zm3.53 4.98a.8.8 0 1 1 0-1.6.8.8 0 0 1 0 1.6zm-6.42 0a.8.8 0 1 1 0-1.6.8.8 0 0 1 0 1.6zm9.51 1.51c-3.2 0-5.79 2.16-5.79 4.83s2.59 4.83 5.79 4.83c.65 0 1.27-.09 1.86-.24l1.7.94-.47-1.52c1.42-1.06 2.34-2.66 2.34-4.51 0-2.67-2.59-4.83-5.79-4.83zm-1.92 4.03a.7.7 0 1 1 0-1.4.7.7 0 0 1 0 1.4zm3.86 0a.7.7 0 1 1 0-1.4.7.7 0 0 1 0 1.4z"/>
                    </svg>
                    <div className="text-xs font-semibold text-eucalyptus-800 uppercase tracking-wider">{t('WeChat · 微信', '微信')}</div>
                  </div>
                  <div className="text-2xl font-bold text-navy-900 font-mono tracking-wide break-all" data-testid="contact-wechat">
                    {companyInfo.wechat}
                  </div>
                  <div className="text-sm text-eucalyptus-700 mt-1">
                    {t('Scan or search to add us', '扫码或搜索添加我们')}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right: Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-3"
          >
            <div className="bg-gray-50 rounded-3xl p-8 lg:p-10 border border-gray-100">
              {status === 'success' ? (
                <div className="py-16 text-center" data-testid="success-message">
                  <div className="w-16 h-16 rounded-full bg-eucalyptus-100 flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-8 h-8 text-eucalyptus-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-navy-900 mb-2">
                    {t('Thank you!', '谢谢您!')}
                  </h3>
                  <p className="text-gray-600">
                    {t('A coordinator will contact you within one business day.', '协调员将在一个工作日内与您联系。')}
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form" noValidate>
                  {/* Honeypot field - hidden from real users, bots will fill it */}
                  <div className="absolute -left-[9999px] w-0 h-0 overflow-hidden" aria-hidden="true">
                    <label htmlFor="website">Website (leave blank)</label>
                    <input
                      type="text"
                      id="website"
                      name="website"
                      tabIndex={-1}
                      autoComplete="off"
                      value={formData.website}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-semibold text-navy-900 mb-2">
                        {t('Full Name', '姓名')} *
                      </label>
                      <input
                        type="text"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full px-4 py-3.5 bg-white border-2 border-gray-200 rounded-2xl focus:border-eucalyptus-500 focus:outline-none transition-all"
                        placeholder={t('Your name', '请输入您的姓名')}
                        data-testid="input-name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-navy-900 mb-2">
                        {t('Phone', '电话')} *
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        required
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3.5 bg-white border-2 border-gray-200 rounded-2xl focus:border-eucalyptus-500 focus:outline-none transition-all"
                        placeholder="04XX XXX XXX"
                        data-testid="input-phone"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-navy-900 mb-2">
                      {t('Email', '邮箱')}
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3.5 bg-white border-2 border-gray-200 rounded-2xl focus:border-eucalyptus-500 focus:outline-none transition-all"
                      placeholder="you@example.com"
                      data-testid="input-email"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-navy-900 mb-2">
                      {t('Preferred Language', '优先语言')}
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { val: 'english', en: 'English', zh: '英语' },
                        { val: 'mandarin', en: 'Mandarin', zh: '普通话' },
                        { val: 'cantonese', en: 'Cantonese', zh: '粤语' }
                      ].map(opt => (
                        <label key={opt.val} className={`flex items-center justify-center px-4 py-3 border-2 rounded-2xl cursor-pointer transition-all ${
                          formData.language === opt.val
                            ? 'border-eucalyptus-500 bg-eucalyptus-50 text-eucalyptus-800 font-semibold'
                            : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                        }`}>
                          <input
                            type="radio"
                            name="language"
                            value={opt.val}
                            checked={formData.language === opt.val}
                            onChange={handleChange}
                            className="sr-only"
                          />
                          <span className="text-sm">{language === 'zh' ? opt.zh : opt.en}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-navy-900 mb-2">
                      {t('Tell us about your care needs', '告诉我们您的护理需求')}
                    </label>
                    <textarea
                      name="message"
                      rows={4}
                      value={formData.message}
                      onChange={handleChange}
                      className="w-full px-4 py-3.5 bg-white border-2 border-gray-200 rounded-2xl focus:border-eucalyptus-500 focus:outline-none transition-all resize-none"
                      placeholder={t('Who are we supporting, and how can we help?', '我们将为谁提供服务，我们如何帮助您？')}
                      data-testid="input-message"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={status === 'submitting'}
                    className="w-full flex items-center justify-center gap-3 px-8 py-4 bg-navy-900 hover:bg-navy-800 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-full font-semibold transition-all shadow-lg hover:shadow-xl"
                    data-testid="submit-button"
                  >
                    {status === 'submitting' ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        {t('Sending...', '发送中...')}
                      </>
                    ) : (
                      <>
                        {t('Send Enquiry', '发送咨询')}
                        <Send className="w-4 h-4" />
                      </>
                    )}
                  </button>

                  {status === 'error' && (
                    <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-2xl" data-testid="error-message">
                      <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-red-800">{errorMsg}</p>
                    </div>
                  )}

                  <p className="text-xs text-gray-500 text-center">
                    {t(
                      'By submitting, you agree to be contacted by AU YH Care. Free consultation, no obligation.',
                      '提交即表示您同意 AU YH Care 与您联系。免费咨询，无需义务。'
                    )}
                  </p>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
